'use client';

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { clsx } from 'clsx';

import { Flag, Icons } from '@monoimin/atoms';

import { getCountryName } from '@monoimin/utils/formatters';

import type { Locales, PhoneCountry } from '@monoimin/sb-types';

type PhoneCodesSelectProps = {
  classNames?: {
    container?: string;
    options?: string;
  };
  selectedValue: PhoneCountry | null;
  setSelectedValue: (value: PhoneCountry) => void;
  phoneCountries: Array<PhoneCountry>;
  locale: Locales;
};

export function PhoneCodesSelect({
  selectedValue,
  setSelectedValue,
  classNames,
  phoneCountries,
  locale,
}: PhoneCodesSelectProps) {
  return (
    <Listbox
      data-testid='phone-codes-select'
      value={selectedValue}
      onChange={setSelectedValue}
      as='div'
      className={clsx('flex items-center relative', classNames?.container)}
    >
      <ListboxButton
        data-testid='phone-codes-select-button'
        className='relative w-full flex gap-1 group'
      >
        {selectedValue ? (
          <Flag countryCode={selectedValue.countryCode} />
        ) : (
          <div
            style={{ width: 33, height: 19 }}
            className='flex justify-center'
          >
            <Icons icon='lang' className='opacity-40 ' />
          </div>
        )}
        <Icons
          icon='dropdown'
          className='transform duration-75 group-data-[open]:rotate-180'
        />
      </ListboxButton>
      <ListboxOptions
        as='ul'
        data-testid='phone-codes-select-options'
        className={clsx(
          'flex flex-col origin-top absolute mt-2 top-8 lg:top-12 border border-line max-h-60 w-[300px] lg:w-[550px] overflow-auto bg-white text-base shadow-lg focus:outline-hidden z-20',
          classNames?.options
        )}
      >
        {phoneCountries.map((item) => {
          const { countryCode, callingCode } = item;

          return (
            <ListboxOption
              as='li'
              data-testid={`phone-codes-select-option-${countryCode}`}
              key={countryCode}
              className='cursor-pointer flex items-center gap-2 px-2.5 py-2 data-[focus]:bg-grey data-[selected]:bg-grey transition-all hover:bg-grey'
              value={item}
            >
              <Flag countryCode={countryCode} />
              <span className='text-lg lg:text-xl'>
                {getCountryName(countryCode, locale)}
              </span>
              <p className='ml-auto'>+{callingCode}</p>
            </ListboxOption>
          );
        })}
      </ListboxOptions>
    </Listbox>
  );
}

export default PhoneCodesSelect;
